import { useState, useEffect, useCallback } from "react";
import {
	DebugStep,
	DebugStatus,
	ExecutionResult,
	InputRequest,
	DebugResult,
} from "../types";

export const usePyodide = () => {
	const [pyodide, setPyodide] = useState<any>(null);
	const [isReady, setIsReady] = useState(false);
	const [status, setStatus] = useState<DebugStatus>("loading");
	const [statusText, setStatusText] = useState("Loading Python environment...");
	const [inputRequest, setInputRequest] = useState<InputRequest | null>(null);

	const updateStatus = useCallback((type: DebugStatus, text: string) => {
		setStatus(type);
		setStatusText(text);
	}, []);

	const handleInputRequest = useCallback((prompt: string): Promise<string> => {
		return new Promise((resolve, reject) => {
			const id = Math.random().toString(36).substr(2, 9);
			setInputRequest({
				id,
				prompt: prompt || "Enter input:",
				resolve,
				reject,
			});
		});
	}, []);

	const submitInput = useCallback(
		(value: string) => {
			if (inputRequest) {
				inputRequest.resolve(value);
				setInputRequest(null);
			}
		},
		[inputRequest],
	);

	const cancelInput = useCallback(() => {
		if (inputRequest) {
			inputRequest.reject(new Error("Input cancelled by user"));
			setInputRequest(null);
		}
	}, [inputRequest]);

	useEffect(() => {
		let isMounted = true;

		const initializePyodide = async () => {
			try {
				// Prevent duplicate initialization
				if (pyodide || !isMounted) {
					return;
				}

				updateStatus("loading", "Loading Python environment...");

				const pyodideInstance = await window.loadPyodide({
					indexURL: "https://cdn.jsdelivr.net/pyodide/v0.27.7/full/",
				});

				// Don't load packages automatically - load them only when imported

				// Set up matplotlib configuration function to be called when matplotlib is loaded
				pyodideInstance.runPython(`
def setup_matplotlib_if_available():
    """Configure matplotlib for web display if it's available"""
    try:
        import matplotlib
        matplotlib.use('Agg')  # Use non-interactive backend
        import matplotlib.pyplot as plt
        import base64
        from io import BytesIO

        # Global storage for plot images
        if '_plot_images' not in globals():
            global _plot_images
            _plot_images = []

        # Override plt.show to capture plots
        _original_show = plt.show

        def custom_show(*args, **kwargs):
            global _plot_images
            
            # Get current figure
            fig = plt.gcf()
            
            # Save figure to bytes
            buf = BytesIO()
            fig.savefig(buf, format='png', bbox_inches='tight', dpi=100)
            buf.seek(0)
            
            # Convert to base64
            img_base64 = base64.b64encode(buf.getvalue()).decode('utf-8')
            _plot_images.append(f"data:image/png;base64,{img_base64}")
            
            # Clear the figure to free memory
            plt.clf()
            
            return None

        # Replace plt.show with our custom version
        plt.show = custom_show

        def get_plot_images():
            global _plot_images
            return _plot_images.copy() if '_plot_images' in globals() else []

        def clear_plot_images():
            global _plot_images
            if '_plot_images' in globals():
                _plot_images.clear()
        
        # Make functions available globally
        globals()['get_plot_images'] = get_plot_images
        globals()['clear_plot_images'] = clear_plot_images
        
    except ImportError:
        # matplotlib not available, create dummy functions
        def get_plot_images():
            return []
        
        def clear_plot_images():
            pass
            
        globals()['get_plot_images'] = get_plot_images
        globals()['clear_plot_images'] = clear_plot_images

# Set up matplotlib configuration
setup_matplotlib_if_available()
        `);

				// Set up the input handler for the React interface
				pyodideInstance.globals.set(
					"__react_input_handler",
					handleInputRequest,
				);

				// Set up global input request function
				(window as any).pyodideInputRequest = (prompt: string) => {
					return handleInputRequest(prompt);
				};

				// Setup the debugger tracer and custom input function
				pyodideInstance.runPython(`
import sys
import json
import traceback
from io import StringIO
import contextlib
import builtins

# Store pending input requests
_pending_input = None
_input_result = None

# Custom input function that communicates with React
def custom_input(prompt=""):
    global _pending_input, _input_result
    
    # For now, we'll handle this synchronously by throwing an exception
    # that the React app can catch and handle
    raise Exception(f"INPUT_REQUEST:{prompt}")

# Replace the built-in input function using setattr to work with both module and dict __builtins__
setattr(builtins, 'input', custom_input)

class DebugTracer:
    def __init__(self):
        self.steps = []
        self.current_step = 0
        self.max_steps = 1000
        self.output_capture = StringIO()
        self.original_stdout = sys.stdout
        self.original_stderr = sys.stderr
        
    def start_output_capture(self):
        self.output_capture = StringIO()
        sys.stdout = self.output_capture
        sys.stderr = self.output_capture
        
    def get_current_output(self):
        return self.output_capture.getvalue()
        
    def stop_output_capture(self):
        sys.stdout = self.original_stdout
        sys.stderr = self.original_stderr
        
    def trace_calls(self, frame, event, arg):
        if len(self.steps) >= self.max_steps:
            return None
            
        if event in ('line', 'call', 'return'):
            step_info = self.capture_frame_info(frame, event, arg)
            self.steps.append(step_info)
            
        return self.trace_calls
    
    def capture_frame_info(self, frame, event, arg):
        lineno = frame.f_lineno
        filename = frame.f_code.co_filename
        func_name = frame.f_code.co_name
        
        local_vars = {}
        for name, value in frame.f_locals.items():
            if not name.startswith('__'):
                local_vars[name] = self.serialize_value(value)
        
        global_vars = {}
        for name, value in frame.f_globals.items():
            if not name.startswith('__') and not callable(value) and name not in ['sys', 'os', 'traceback', 'json']:
                global_vars[name] = self.serialize_value(value)
        
        stack = []
        current_frame = frame
        while current_frame:
            stack.append({
                'function': current_frame.f_code.co_name,
                'line': current_frame.f_lineno,
                'locals': {name: self.serialize_value(val) 
                          for name, val in current_frame.f_locals.items() 
                          if not name.startswith('__')}
            })
            current_frame = current_frame.f_back
            if len(stack) > 10:
                break
        
        return {
            'step': len(self.steps),
            'event': event,
            'line': lineno,
            'function': func_name,
            'filename': filename,
            'locals': local_vars,
            'globals': global_vars,
            'stack': list(reversed(stack)),
            'return_value': self.serialize_value(arg) if event == 'return' else None,
            'cumulativeOutput': self.get_current_output()
        }
    
    def serialize_value(self, value):
        try:
            if value is None:
                return 'None'
            elif isinstance(value, (int, float, bool)):
                return str(value)
            elif isinstance(value, str):
                # Always show full string, let frontend handle truncation
                display_value = repr(value)
                return {
                    'name': name,
                    'type': 'str',
                    'value': display_value,
                    'id': str(id(value))
                }
            elif isinstance(value, (list, tuple)):
                # Always show actual content, let frontend handle truncation
                try:
                    items = []
                    for item in value:
                        items.append(self.serialize_value(item))
                    if isinstance(value, list):
                        return '[' + ', '.join(items) + ']'
                    else:
                        return '(' + ', '.join(items) + (',' if len(value) == 1 else '') + ')'
                except:
                    return f'[{len(value)} items]' if isinstance(value, list) else f'({len(value)} items)'
            elif isinstance(value, dict):
                # Always show actual content, let frontend handle truncation
                try:
                    items = []
                    for k, v in value.items():
                        key_str = str(k)
                        val_str = self.serialize_value(v)
                        items.append(f'{key_str}: {val_str}')
                    return '{' + ', '.join(items) + '}'
                except:
                    return f'{{{len(value)} items}}'
            elif hasattr(value, '__class__'):
                return f'<{value.__class__.__name__} object>'
            else:
                return str(value)[:50]
        except:
            return '<unable to serialize>'
    
    def get_steps(self):
        return self.steps
    
    def reset(self):
        self.steps = []
        self.current_step = 0
        self.output_capture = StringIO()

_debug_tracer = DebugTracer()

def start_trace():
    _debug_tracer.reset()
    _debug_tracer.start_output_capture()
    sys.settrace(_debug_tracer.trace_calls)

def stop_trace():
    sys.settrace(None)
    _debug_tracer.stop_output_capture()

def get_debug_steps():
    # Convert steps to JSON-serializable format
    steps = []
    for step in _debug_tracer.steps:
        cumulative_output = step['cumulativeOutput']
        steps.append({
            'lineNumber': step['line'],
            'event': step['event'],
            'function': step['function'],
            'localVars': step['locals'],
            'globalVars': step['globals'],
            'stack': step['stack'],
            'cumulativeOutput': cumulative_output
        })
    return steps
        `);

				if (isMounted) {
					setPyodide(pyodideInstance);
					setIsReady(true);
					updateStatus("ready", "Ready to debug");
				}
			} catch (error) {
				console.error("Failed to initialize Pyodide:", error);
				if (isMounted) {
					updateStatus("error", "Failed to load Python environment");
				}
			}
		};

		initializePyodide();

		return () => {
			isMounted = false;
		};
	}, [updateStatus, handleInputRequest, pyodide]);

	const runCode = useCallback(
		async (code: string): Promise<ExecutionResult> => {
			if (!pyodide || !isReady) {
				return {
					success: false,
					output: "",
					error: "Python environment not ready",
				};
			}

			try {
				// Load packages from imports first
				await pyodide.loadPackagesFromImports(code);

				// Set up matplotlib if it was loaded
				pyodide.runPython("setup_matplotlib_if_available()");

				// Clean up any previous execution state
				pyodide.runPython(`
import sys
import builtins

# Reset to clean state
if 'captured_output' in globals():
    del globals()['captured_output']
if '_input_values' in globals():
    del globals()['_input_values']
if '_input_index' in globals():
    del globals()['_input_index']

# Restore original input function if it exists
if hasattr(builtins, 'input') and hasattr(builtins.input, '__name__') and builtins.input.__name__ == 'managed_input':
    if 'custom_input' in globals():
        setattr(builtins, 'input', custom_input)
      `);

				// Set up clean execution environment and input handling
				pyodide.runPython(`
import sys
from io import StringIO
import contextlib
import builtins

# Create fresh output capture
captured_output = StringIO()

# Input storage
_input_values = []
_input_index = 0

def managed_input(prompt=""):
    global _input_values, _input_index
    if _input_index < len(_input_values):
        result = _input_values[_input_index]
        _input_index += 1
        print(prompt + result)
        return result
    else:
        raise Exception(f"INPUT_REQUEST:{prompt}")

# Temporarily replace input
_temp_original = getattr(builtins, 'input', None)
setattr(builtins, 'input', managed_input)
      `);

				pyodide.globals.set("user_code", code);
				const inputValues: string[] = [];

				// Input collection loop
				while (true) {
					try {
						pyodide.runPython(`
_input_index = 0
captured_output = StringIO()

with contextlib.redirect_stdout(captured_output):
    with contextlib.redirect_stderr(captured_output):
        exec(user_code)
          `);
						break; // Success, exit loop
					} catch (error: any) {
						if (error.toString().includes("INPUT_REQUEST:")) {
							const prompt =
								error.toString().split("INPUT_REQUEST:")[1] || "Enter input:";
							const userInput = await handleInputRequest(prompt);
							inputValues.push(userInput);
							pyodide.globals.set("_input_values", inputValues);
							pyodide.runPython('globals()["_input_values"] = _input_values');
						} else {
							throw error; // Re-throw non-input errors
						}
					}
				}

				const output = pyodide.runPython("captured_output.getvalue()");

				// Get plot images
				const plotImages = pyodide.runPython("get_plot_images()");

				// Clear plot images for next execution
				pyodide.runPython("clear_plot_images()");

				// Restore input function
				pyodide.runPython(`
if _temp_original:
    setattr(builtins, 'input', _temp_original)
else:
    setattr(builtins, 'input', custom_input)
      `);

				return {
					success: true,
					output: output || "Code executed successfully (no output)",
					error: "",
					plotImages: plotImages || [],
				};
			} catch (error: any) {
				return {
					success: false,
					output: "",
					error: error.toString(),
					plotImages: [],
				};
			}
		},
		[pyodide, isReady, handleInputRequest],
	);

	const startDebugging = useCallback(
		async (code: string): Promise<DebugResult> => {
			if (!pyodide || !isReady) {
				return {
					success: false,
					error: "Python environment not ready",
					steps: [],
				};
			}

			try {
				// Load packages from imports first
				await pyodide.loadPackagesFromImports(code);

				// Set up matplotlib if it was loaded
				pyodide.runPython("setup_matplotlib_if_available()");

				// Clean up any previous debugging state
				pyodide.runPython(`
import sys
import builtins
import ast
import types
from io import StringIO
import contextlib

# Clear any previous debugging state - selective cleanup
__pyblaze_essential_vars = {
    '__builtins__', '__name__', '__doc__', '__package__', '__loader__',
    '__spec__', '__file__', '__cached__', '__annotations__', '__dict__',
    'builtins', 'user_code'  # Keep essential Python modules
}

for __pyblaze_cleanup_name in list(globals().keys()):
    # Only clean up our own debugging variables and specific known artifacts
    if (__pyblaze_cleanup_name.startswith('__pyblaze_') or 
        __pyblaze_cleanup_name.startswith('_debug_') or 
        __pyblaze_cleanup_name in ['DebugTracer', 'start_trace', 'stop_trace', 'get_debug_steps']):
        try:
            del globals()[__pyblaze_cleanup_name]
        except:
            pass  # Ignore errors if variable can't be deleted

# Restore input function for debugging
if 'custom_input' in globals():
    setattr(builtins, 'input', custom_input)
      `);

				// Set the user code for parsing
				pyodide.globals.set("user_code", code);

				// New comprehensive debugging tracer
				pyodide.runPython(`
# Parse the code to get line numbers and track imports
__pyblaze_parsed_code = compile(user_code, '<exec>', 'exec')
__pyblaze_code_lines = user_code.split('\\n')
__pyblaze_steps = []
__pyblaze_step_count = 0
__pyblaze_previous_line_info = None

# Import StringIO for output capture (in a way that doesn't pollute globals)
import io as __pyblaze_io
__pyblaze_output = __pyblaze_io.StringIO()

# Track user imports by parsing the source code
import ast as __pyblaze_ast
__pyblaze_user_imports = set()

try:
    __pyblaze_tree = __pyblaze_ast.parse(user_code)
    for __pyblaze_node in __pyblaze_ast.walk(__pyblaze_tree):
        if isinstance(__pyblaze_node, __pyblaze_ast.Import):
            for __pyblaze_alias in __pyblaze_node.names:
                __pyblaze_user_imports.add(__pyblaze_alias.name.split('.')[0])
        elif isinstance(__pyblaze_node, __pyblaze_ast.ImportFrom):
            if __pyblaze_node.module:
                __pyblaze_user_imports.add(__pyblaze_node.module.split('.')[0])
except:
    pass  # If parsing fails, continue without import tracking

def __pyblaze_get_variable_info(name, value):
    """Get detailed information about a variable"""
    try:
        var_type = type(value).__name__
        
        # Handle different types appropriately
        if value is None:
            return {
                'name': name,
                'type': 'NoneType',
                'value': 'None',
                'id': str(id(value))
            }
        elif isinstance(value, bool):
            return {
                'name': name,
                'type': 'bool',
                'value': str(value),
                'id': str(id(value))
            }
        elif isinstance(value, int):
            return {
                'name': name,
                'type': 'int',
                'value': str(value),
                'id': str(id(value))
            }
        elif isinstance(value, float):
            return {
                'name': name,
                'type': 'float',
                'value': str(value),
                'id': str(id(value))
            }
        elif isinstance(value, str):
            # Always show full string, let frontend handle truncation
            display_value = repr(value)
            return {
                'name': name,
                'type': 'str',
                'value': display_value,
                'id': str(id(value))
            }
        elif isinstance(value, list):
            # Always show actual content, let frontend handle truncation
            try:
                items = []
                for item in value:
                    if isinstance(item, str):
                        items.append(repr(item))
                    else:
                        items.append(str(item))
                display_value = '[' + ', '.join(items) + ']'
            except:
                display_value = f'[{len(value)} items]'  # fallback if serialization fails
            return {
                'name': name,
                'type': 'list',
                'value': display_value,
                'id': str(id(value))
            }
        elif isinstance(value, tuple):
            # Always show actual content, let frontend handle truncation
            try:
                items = []
                for item in value:
                    if isinstance(item, str):
                        items.append(repr(item))
                    else:
                        items.append(str(item))
                display_value = '(' + ', '.join(items) + (',' if len(value) == 1 else '') + ')'
            except:
                display_value = f'({len(value)} items)'  # fallback if serialization fails
            return {
                'name': name,
                'type': 'tuple',
                'value': display_value,
                'id': str(id(value))
            }
        elif isinstance(value, dict):
            # Always show actual content, let frontend handle truncation
            try:
                items = []
                for k, v in value.items():
                    key_str = repr(k) if isinstance(k, str) else str(k)
                    val_str = repr(v) if isinstance(v, str) else str(v)
                    items.append(f'{key_str}: {val_str}')
                display_value = '{' + ', '.join(items) + '}'
            except:
                display_value = f'{{{len(value)} items}}'  # fallback if serialization fails
            return {
                'name': name,
                'type': 'dict',
                'value': display_value,
                'id': str(id(value))
            }
        elif isinstance(value, set):
            # Always show actual content, let frontend handle truncation
            try:
                items = []
                for item in value:
                    if isinstance(item, str):
                        items.append(repr(item))
                    else:
                        items.append(str(item))
                display_value = '{' + ', '.join(items) + '}'
            except:
                display_value = f'set({len(value)} items)'  # fallback if serialization fails
            return {
                'name': name,
                'type': 'set',
                'value': display_value,
                'id': str(id(value))
            }
        elif callable(value):
            return {
                'name': name,
                'type': 'function',
                'value': f'<function {value.__name__}>',
                'id': str(id(value))
            }
        else:
            return {
                'name': name,
                'type': var_type,
                'value': f'<{var_type} object>',
                'id': str(id(value))
            }
    except Exception:
        return {
            'name': name,
            'type': 'unknown',
            'value': '<error getting value>',
            'id': 'unknown'
        }

def __pyblaze_filter_variables(variables_dict, is_global=False):
    """Filter out internal variables and return VariableInfo objects"""
    filtered = {}
    
    # Internal variables to always exclude
    internal_vars = {
        '__builtins__', '__name__', '__doc__', '__package__', '__loader__',
        '__spec__', '__file__', '__cached__', '__annotations__', '__dict__',
        'user_code', 'captured_output', '_input_values', '_input_index', 
        '_temp_original', 'managed_input', 'custom_input', 'setup_matplotlib_if_available',
        'get_plot_images', 'clear_plot_images',
        # Old debugging artifacts that might still exist
        'StringIO', 'DebugTracer', 'start_trace', 'stop_trace', 'get_debug_steps',
        '_debug_tracer', 'contextlib', 'sys', 'json', 'traceback', 'ast',
        # Python built-in modules that shouldn't appear
        'builtins', 'types', 'collections', 'itertools', 'functools', 're', 'os',
        'math', 'random', 'datetime', 'time', 'urllib', 'pathlib', 'typing',
        'abc', 'dataclasses', 'enum', 'logging', 'unittest', 'io', 'csv',
        'xml', 'html', 'http', 'email', 'base64', 'hashlib', 'hmac', 'secrets',
        'uuid', 'calendar', 'locale', 'gettext', 'argparse', 'configparser',
        # Python built-in types and functions that shouldn't appear as variables
        'print', 'len', 'range', 'list', 'dict', 'set', 'tuple', 'str', 'int', 
        'float', 'bool', 'type', 'isinstance', 'hasattr', 'getattr', 'setattr', 
        'delattr', 'dir', 'help', 'id', 'hash', 'abs', 'min', 'max', 'sum',
        'sorted', 'reversed', 'enumerate', 'zip', 'map', 'filter', 'open',
        'input', 'exec', 'eval', 'compile', 'globals', 'locals', 'vars',
        'bytes', 'bytearray', 'memoryview', 'complex', 'frozenset', 'object',
        'property', 'slice', 'staticmethod', 'classmethod', 'super'
    }
    
    # Add pyBlaze-related variables
    pyblaze_vars = {name for name in variables_dict.keys() if name.startswith('__pyblaze_')}
    internal_vars.update(pyblaze_vars)
    
    for name, value in variables_dict.items():
        # Skip internal variables
        if name in internal_vars:
            continue
            
        # Skip all variables starting with underscore (including our __pyblaze_ ones)
        if name.startswith('_'):
            continue
            
        # For globals, be very restrictive - only show user-defined variables
        if is_global:
            # Skip built-in functions and types
            if callable(value) and hasattr(value, '__module__') and value.__module__ == 'builtins':
                continue
                
            # Skip all modules completely (both stdlib and user modules)
            if hasattr(value, '__name__') and (hasattr(value, '__file__') or hasattr(value, '__package__')):
                continue
                
            # Skip any object that looks like a module (more comprehensive check)
            if (str(type(value)).startswith("<class 'module") or 
                hasattr(value, '__name__') and hasattr(value, '__file__') or
                hasattr(value, '__package__') or
                str(value).startswith('<module ')):
                continue
                
            # Skip imported classes and functions from other modules
            if callable(value) and hasattr(value, '__module__'):
                module_name = getattr(value, '__module__', '')
                # Only allow functions defined in the main execution context
                if module_name not in ['__main__', '<exec>', None]:
                    continue
                    
            # Skip classes that aren't user-defined
            if hasattr(value, '__class__') and hasattr(value.__class__, '__module__'):
                class_module = getattr(value.__class__, '__module__', '')
                if class_module and class_module not in ['__main__', '<exec>', 'builtins']:
                    # This is likely an imported class instance
                    continue
                    
            # Skip any type objects (classes themselves) unless user-defined
            if isinstance(value, type):
                type_module = getattr(value, '__module__', '')
                if type_module not in ['__main__', '<exec>', None]:
                    continue
                    
            # Skip built-in objects by checking their string representation
            value_str = str(value)
            if (value_str.startswith('<built-in') or 
                value_str.startswith('<class ') or
                value_str.startswith('<function ') and 'built-in' in value_str):
                continue
                    
            # Final safeguard: For globals, be very conservative with functions/classes
            if is_global and (callable(value) or isinstance(value, type)):
                # Only allow if the name appears to be user-defined (simple heuristic)
                if (name.startswith('_') or 
                    name[0].isupper() or  # Likely a class name from imports
                    hasattr(value, '__module__') and getattr(value, '__module__') not in ['__main__', '<exec>', None]):
                    continue
        
        # Final check: Skip variables with internal values
        value_str = str(value)
        if ('__pyblaze_' in value_str or 
            '_debug_' in value_str or
            value_str.startswith('<module ') or
            value_str.startswith('<built-in')):
            continue
        
        # Only add variables that pass all filters
        filtered[name] = __pyblaze_get_variable_info(name, value)
    
    return filtered

# Store the previous line info to show results AFTER execution
__pyblaze_previous_line_info = None

def __pyblaze_tracer(frame, event, arg):
    global __pyblaze_step_count, __pyblaze_steps, __pyblaze_output, __pyblaze_previous_line_info
    
    filename = frame.f_code.co_filename
    
    # Only trace user code
    if filename != '<exec>':
        return __pyblaze_tracer
    
    # Filter events to reduce confusion
    function_name = frame.f_code.co_name
    
    # For main module, only show line events
    if function_name == '<module>' and event != 'line':
        return __pyblaze_tracer
    
    # For user functions, show call, line, and return - but be selective
    if event in ('line', 'call', 'return'):
        __pyblaze_lineno = frame.f_lineno
        
        # Get current output
        __pyblaze_current_output = __pyblaze_output.getvalue()
        
        # If this is a line event and we have a previous line, 
        # create a step showing the result of the previous line execution
        if event == 'line' and __pyblaze_previous_line_info is not None:
            # Get current variable state (which shows the result of the previous line)
            __pyblaze_local_vars = __pyblaze_filter_variables(frame.f_locals, is_global=False)
            __pyblaze_global_vars = __pyblaze_filter_variables(frame.f_globals, is_global=True)
            
            # Build stack frames
            __pyblaze_stack_frames = []
            __pyblaze_current_frame = frame
            __pyblaze_frame_index = 0
            
            while __pyblaze_current_frame and __pyblaze_current_frame.f_code.co_filename == '<exec>':
                __pyblaze_frame_locals = __pyblaze_filter_variables(__pyblaze_current_frame.f_locals, is_global=False)
                
                __pyblaze_stack_frames.append({
                    'function': __pyblaze_current_frame.f_code.co_name,
                    'lineNumber': __pyblaze_previous_line_info['lineNumber'],  # Show the previous line that just executed
                    'locals': __pyblaze_frame_locals,
                    'isCurrentFrame': __pyblaze_frame_index == 0
                })
                
                __pyblaze_current_frame = __pyblaze_current_frame.f_back
                __pyblaze_frame_index += 1
                
                # Limit stack depth
                if __pyblaze_frame_index > 10:
                    break
            
            # Create step showing the result of the previous line
            __pyblaze_step = {
                'stepNumber': __pyblaze_step_count,
                'event': __pyblaze_previous_line_info['event'],
                'lineNumber': __pyblaze_previous_line_info['lineNumber'],
                'function': __pyblaze_previous_line_info['function'],
                'filename': '<exec>',
                'localVars': __pyblaze_local_vars,
                'globalVars': __pyblaze_global_vars,
                'stack': __pyblaze_stack_frames,
                'returnValue': None,
                'output': __pyblaze_current_output
            }
            
            __pyblaze_steps.append(__pyblaze_step)
            __pyblaze_step_count += 1
        
        # For non-line events (call, return), create step immediately
        if event != 'line':
            # Get variables in current frame
            __pyblaze_local_vars = __pyblaze_filter_variables(frame.f_locals, is_global=False)
            __pyblaze_global_vars = __pyblaze_filter_variables(frame.f_globals, is_global=True)
            
            # Build stack frames
            __pyblaze_stack_frames = []
            __pyblaze_current_frame = frame
            __pyblaze_frame_index = 0
            
            while __pyblaze_current_frame and __pyblaze_current_frame.f_code.co_filename == '<exec>':
                __pyblaze_frame_locals = __pyblaze_filter_variables(__pyblaze_current_frame.f_locals, is_global=False)
                
                __pyblaze_stack_frames.append({
                    'function': __pyblaze_current_frame.f_code.co_name,
                    'lineNumber': __pyblaze_current_frame.f_lineno,
                    'locals': __pyblaze_frame_locals,
                    'isCurrentFrame': __pyblaze_frame_index == 0
                })
                
                __pyblaze_current_frame = __pyblaze_current_frame.f_back
                __pyblaze_frame_index += 1
                
                # Limit stack depth
                if __pyblaze_frame_index > 10:
                    break
            
            # Handle return values
            __pyblaze_return_value = None
            if event == 'return' and arg is not None:
                __pyblaze_return_value = __pyblaze_get_variable_info('return_value', arg)
            
            __pyblaze_step = {
                'stepNumber': __pyblaze_step_count,
                'event': event,
                'lineNumber': __pyblaze_lineno,
                'function': function_name,
                'filename': '<exec>',
                'localVars': __pyblaze_local_vars,
                'globalVars': __pyblaze_global_vars,
                'stack': __pyblaze_stack_frames,
                'returnValue': __pyblaze_return_value,
                'output': __pyblaze_current_output
            }
            
            __pyblaze_steps.append(__pyblaze_step)
            __pyblaze_step_count += 1
        
        # Store current line info for next iteration (for line events)
        if event == 'line':
            __pyblaze_previous_line_info = {
                'lineNumber': __pyblaze_lineno,
                'function': function_name,
                'event': event
            }
        
        # Limit total steps to prevent runaway programs
        if __pyblaze_step_count > 1000:
            raise Exception("Debug session exceeded maximum steps (1000)")
    
    return __pyblaze_tracer

# Execute with tracing
# Note: no manual initial step is added. The deferred-step model already emits a
# step for the first line (showing its result) once the second line event fires,
# so adding an initial line-1 step here would double-count the first statement.
sys.settrace(__pyblaze_tracer)

try:
    with contextlib.redirect_stdout(__pyblaze_output):
        with contextlib.redirect_stderr(__pyblaze_output):
            exec(__pyblaze_parsed_code)
except Exception as e:
    # Capture the error in output
    __pyblaze_output.write(f"Error: {e}\\n")
finally:
    sys.settrace(None)
    
    # Capture the final state after execution completes
    # This ensures we get the final variable values after the last statement
    __pyblaze_final_output = __pyblaze_output.getvalue()
    
    # If we have a pending line that hasn't been processed, process it now
    if __pyblaze_previous_line_info is not None:
        # Get the final variable state in the execution context
        __pyblaze_final_globals = __pyblaze_filter_variables(globals(), is_global=True)
        __pyblaze_final_locals = __pyblaze_filter_variables(locals(), is_global=False)
        
        # Create final step showing the result of the last line execution
        __pyblaze_final_step = {
            'stepNumber': __pyblaze_step_count,
            'event': __pyblaze_previous_line_info['event'],
            'lineNumber': __pyblaze_previous_line_info['lineNumber'],
            'function': __pyblaze_previous_line_info['function'],
            'filename': '<exec>',
            'localVars': __pyblaze_final_locals,
            'globalVars': __pyblaze_final_globals,
            'stack': [{
                'function': '<module>',
                'lineNumber': __pyblaze_previous_line_info['lineNumber'],
                'locals': __pyblaze_final_locals,
                'isCurrentFrame': True
            }],
            'returnValue': None,
            'output': __pyblaze_final_output
        }
        
        # Always append the final step: the last line is never emitted during
        # tracing (no later line event fires), so this is its only representation.
        # A previous var-snapshot dedup here dropped final statements that don't
        # mutate variables (e.g. print(x), bare calls, assert).
        __pyblaze_steps.append(__pyblaze_final_step)
      `);

				// Get the debug steps
				const stepsJson = pyodide.runPython(
					"import json; json.dumps(__pyblaze_steps)",
				);
				const steps: DebugStep[] = JSON.parse(stepsJson);

				return { success: true, error: "", steps };
			} catch (error: any) {
				console.error("Failed to trace code:", error);
				return { success: false, error: error.toString(), steps: [] };
			}
		},
		[pyodide, isReady],
	);

	const uploadFile = useCallback(
		(fileName: string, data: Uint8Array) => {
			if (pyodide && isReady) {
				pyodide.FS.writeFile(fileName, data);
			}
		},
		[pyodide, isReady],
	);

	const removeFile = useCallback(
		(fileName: string) => {
			if (pyodide && isReady) {
				try {
					pyodide.FS.unlink(fileName);
				} catch (error) {
					console.warn("Could not remove file from Pyodide filesystem:", error);
				}
			}
		},
		[pyodide, isReady],
	);

	const clearDebugState = useCallback(() => {
		if (pyodide && isReady) {
			try {
				// Comprehensive cleanup of all debugging state AND user variables
				pyodide.runPython(`
# Clear all debugging variables and user variables for fresh session
try:
    # Store essential system variables that should not be cleared
    __essential_vars = {
        '__builtins__', '__name__', '__doc__', '__package__', '__loader__', 
        '__spec__', '__annotations__', '__cached__', '__file__',
        'setup_matplotlib_if_available', 'get_plot_images', 'clear_plot_images',
        'custom_input', '__react_input_handler'
    }
    
    # Get list of all current global variables
    __all_globals = list(globals().keys())
    
    # Clear debugging variables first
    for __cleanup_name in __all_globals:
        if (__cleanup_name.startswith('__pyblaze_') or 
            __cleanup_name.startswith('_debug_') or 
            __cleanup_name.startswith('__cleanup_') or
            __cleanup_name in ['DebugTracer', 'start_trace', 'stop_trace', 'get_debug_steps']):
            try:
                del globals()[__cleanup_name]
            except:
                pass
    
    # Clear user-defined variables (but preserve essential system vars)
    __all_globals = list(globals().keys())  # Refresh list after cleanup
    for __cleanup_name in __all_globals:
        if (__cleanup_name not in __essential_vars and 
            not __cleanup_name.startswith('__cleanup_')):
            try:
                # Additional check to avoid clearing functions we need
                __cleanup_value = globals()[__cleanup_name]
                if (not callable(__cleanup_value) or 
                    (__cleanup_name not in ['setup_matplotlib_if_available', 'get_plot_images', 
                                           'clear_plot_images', 'custom_input'])):
                    del globals()[__cleanup_name]
            except:
                pass
    
    # Reset tracing
    import sys
    sys.settrace(None)
    
    # Clear plot images if they exist
    if 'clear_plot_images' in globals():
        clear_plot_images()
    
    # Clear any imported modules that aren't essential (optional)
    # This helps ensure a truly fresh environment
    import sys
    __modules_to_remove = []
    for __mod_name in sys.modules.keys():
        if (__mod_name not in ['sys', 'builtins', 'io', 'json', 'traceback', 
                              'contextlib', 'base64', 'math', 'random'] and
            not __mod_name.startswith('_') and
            __mod_name not in ['matplotlib', 'matplotlib.pyplot', 'numpy', 'pandas', 'requests']):
            # Don't remove core modules, but user imports can be cleared
            pass  # We'll be conservative and not remove modules for now
            
    # Clean up our cleanup variables
    for __cleanup_var in ['__essential_vars', '__all_globals', '__modules_to_remove', 
                         '__cleanup_name', '__cleanup_value', '__mod_name']:
        try:
            if __cleanup_var in globals():
                del globals()[__cleanup_var]
        except:
            pass
        
except Exception as e:
    print(f"Debug cleanup warning: {e}")
        `);
			} catch (error) {
				console.warn("Could not clear debug state:", error);
			}
		}
	}, [pyodide, isReady]);

	return {
		isReady,
		status,
		statusText,
		updateStatus,
		runCode,
		startDebugging,
		uploadFile,
		removeFile,
		clearDebugState,
		inputRequest,
		submitInput,
		cancelInput,
	};
};
